import { clsx } from 'clsx';
import type { ReactNode, SVGProps } from 'react';
import { InfoIcon } from './icons.tsx';

/** What the right-hand column explains about the section that is open. */
export interface SectionHelp {
  summary: string;
  /** Non-obvious behaviour worth stating outright — the reason this panel exists. */
  points?: string[];
}

export interface PageTab<T extends string> {
  id: T;
  label: string;
  icon: (props: SVGProps<SVGSVGElement>) => React.JSX.Element;
  help: SectionHelp;
}

/**
 * Header, section navigation and contextual help for the Management, Settings
 * and Inventory screens.
 *
 * Three columns from `xl`: an icon list on the left, the section's cards in the
 * middle, and what the section does on the right. Between `md` and `xl` the
 * help column is dropped rather than squeezed — there is no gutter to put it in
 * at that width, and narrowing the cards to make one would cost more than the
 * text is worth. Below `md` the nav becomes a scrolling row of pills.
 */
export function PageShell<T extends string>({
  title,
  description,
  tabs,
  active,
  onChange,
  actions,
  children,
}: {
  title: string;
  description?: string;
  tabs: PageTab<T>[];
  active: T;
  onChange: (id: T) => void;
  actions?: ReactNode;
  children: ReactNode;
}): React.JSX.Element {
  const current = tabs.find((tab) => tab.id === active);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="border-b border-slate-200 bg-white px-5 py-5 dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-50">{title}</h1>
            {description ? (
              <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">{description}</p>
            ) : null}
          </div>
          {actions}
        </div>
      </header>

      <div className="lg-scroll min-h-0 flex-1 overflow-y-auto bg-slate-50 p-5 dark:bg-slate-950">
        <div className="flex flex-col gap-5 md:flex-row md:gap-8">
          <nav
            aria-label="Sections"
            className="lg-scroll -mx-1 flex shrink-0 gap-1 overflow-x-auto px-1 pb-1
                       md:mx-0 md:w-56 md:flex-col md:self-start md:overflow-visible md:px-0 md:pb-0
                       md:sticky md:top-0"
          >
            {tabs.map((tab) => {
              const isActive = tab.id === active;

              return (
                <button
                  key={tab.id}
                  type="button"
                  aria-current={isActive ? 'page' : undefined}
                  onClick={() => {
                    onChange(tab.id);
                  }}
                  className={clsx(
                    'flex shrink-0 items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition md:w-full',
                    isActive
                      ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200'
                      : 'text-slate-600 hover:bg-slate-200/70 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100',
                  )}
                >
                  <tab.icon
                    className={clsx(
                      'h-4 w-4 shrink-0',
                      isActive ? '' : 'text-slate-400 dark:text-slate-500',
                    )}
                  />
                  {tab.label}
                </button>
              );
            })}
          </nav>

          {/* min-w-0 so a wide table inside a section scrolls instead of
              stretching the whole row. */}
          <div className="min-w-0 flex-1 space-y-5">{children}</div>

          {current ? (
            <aside className="hidden w-72 shrink-0 xl:block xl:sticky xl:top-0 xl:self-start">
              <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-50">
                  <InfoIcon className="h-4 w-4 shrink-0 text-brand-600 dark:text-brand-400" />
                  {current.label}
                </h2>

                <p className="mt-2 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                  {current.help.summary}
                </p>

                {current.help.points && current.help.points.length > 0 ? (
                  <ul className="mt-3 space-y-2 border-t border-slate-100 pt-3 dark:border-slate-800">
                    {current.help.points.map((point) => (
                      <li
                        key={point}
                        className="flex gap-2 text-xs leading-relaxed text-slate-500 dark:text-slate-400"
                      >
                        <span aria-hidden="true" className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-slate-300 dark:bg-slate-600" />
                        {point}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </aside>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function Section({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <section className="lg-card overflow-hidden">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-5 py-4 dark:border-slate-800">
        {/* `min-w-0 flex-1` so a long description wraps inside its own column
            instead of claiming the full width and shoving the buttons onto the
            next line. `ml-auto` keeps them on the right on the narrow screens
            where they do wrap — without it they land under the title, which is
            where a heading goes, not a control. */}
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-50">{title}</h2>
          {description ? (
            <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">{description}</p>
          ) : null}
        </div>
        {actions ? <div className="ml-auto shrink-0">{actions}</div> : null}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

export function Toggle({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}): React.JSX.Element {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <input
        type="checkbox"
        className="lg-checkbox mt-0.5"
        checked={checked}
        onChange={(event) => {
          onChange(event.target.checked);
        }}
      />
      <span>
        <span className="block text-sm font-medium text-slate-800 dark:text-slate-100">{label}</span>
        {description ? (
          <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">{description}</span>
        ) : null}
      </span>
    </label>
  );
}
